import type {
  EmployerFootprintAdminViewDto,
  EmployerVerificationDetail,
} from '../../api/employerVerificationDashboardApi';
import type { ComparedValueRow } from '../checkpointComparedValues';
import { comparedValuesForCheckpointStep } from '../checkpointComparedValues';
import {
  buildAiLinkageAdjudicatorView,
  extractBankPayorFromLinkageReason,
  extractEmployerFromLinkageReason,
} from './parseAiLinkageAnalysis';
import type { ReviewReasonEvidence } from './reviewReason.types';

export type LinkageCheckpointView = {
  popoverTitle: string;
  dataPoints: ReviewReasonEvidence[];
  issueStatement: string;
  issueSeverity: 'warning' | 'error' | 'info';
  issueLabel: string;
  footerNote: string | null;
  advisoryLabel: string | null;
  advisorySeverity: 'warning' | 'success' | 'default';
};

export const LINKAGE_CHECKPOINT_STEPS = [
  'DIRECT_PAYOR_MATCH',
  'PAYROLL_PROVIDER_MATCH',
  'PERSONAL_TRANSFER_DETECTION',
  'OWNER_OPERATOR_MATCH',
  'GOVERNMENT_SCHEME_PAYOR',
  'UNKNOWN_PAYOR',
  'AI_LINKAGE_ANALYSIS',
] as const;

export type LinkageCheckpointStep = (typeof LINKAGE_CHECKPOINT_STEPS)[number];

const STEP_POPOVER_TITLES: Record<LinkageCheckpointStep, string> = {
  DIRECT_PAYOR_MATCH: 'Direct payor match',
  PAYROLL_PROVIDER_MATCH: 'Payroll provider match',
  PERSONAL_TRANSFER_DETECTION: 'Personal transfer check',
  OWNER_OPERATOR_MATCH: 'Owner-operator linkage',
  GOVERNMENT_SCHEME_PAYOR: 'Government payor check',
  UNKNOWN_PAYOR: 'Unknown payor',
  AI_LINKAGE_ANALYSIS: 'AI payroll linkage pre-read',
};

function normalizeStep(step: string): LinkageCheckpointStep | null {
  const u = step.trim().toUpperCase();
  return (LINKAGE_CHECKPOINT_STEPS as readonly string[]).includes(u)
    ? (u as LinkageCheckpointStep)
    : null;
}

export function isLinkageCheckpoint(step: string): boolean {
  return normalizeStep(step) != null;
}

function issueMetaFromBucket(bucket: string): {
  severity: 'warning' | 'error' | 'info';
  label: string;
} {
  if (bucket === 'fail') return { severity: 'error', label: 'Needs action' };
  if (bucket === 'review') return { severity: 'warning', label: 'Review' };
  return { severity: 'info', label: 'Passed' };
}

function pushPoint(
  rows: ReviewReasonEvidence[],
  label: string,
  value: string | null | undefined,
  tone: ReviewReasonEvidence['tone'],
) {
  if (value) rows.push({ label, value, tone });
}

function buildDataPointsForStep(
  step: LinkageCheckpointStep,
  input: {
    detail: EmployerVerificationDetail;
    compared: ComparedValueRow[];
    reason: string;
  },
): ReviewReasonEvidence[] {
  const rows: ReviewReasonEvidence[] = [];
  const compared = new Map(input.compared.map((row) => [row.label, row.value]));
  const employer =
    input.detail.employer?.legalName?.trim() ??
    extractEmployerFromLinkageReason(input.reason);
  const payor =
    extractBankPayorFromLinkageReason(input.reason) ?? compared.get('Bank payor (memo)');

  if (step === 'AI_LINKAGE_ANALYSIS') {
    return buildAiLinkageAdjudicatorView({
      reason: input.reason,
      detail: input.detail,
    }).dataPoints;
  }

  pushPoint(rows, 'Declared employer', employer, 'declared');
  pushPoint(rows, 'Bank payor (memo)', payor, 'observed');
  pushPoint(rows, 'Match result', compared.get('Match result'), 'aiInsight');
  pushPoint(rows, 'Overlap', compared.get('Overlap'), 'aiInsight');

  return rows.slice(0, 4);
}

function buildIssueStatement(
  step: LinkageCheckpointStep,
  reason: string,
  bucket: string,
  detail: EmployerVerificationDetail,
): string {
  if (step === 'AI_LINKAGE_ANALYSIS') {
    return buildAiLinkageAdjudicatorView({ reason, detail }).issueStatement;
  }

  const payor = extractBankPayorFromLinkageReason(reason);
  const employer =
    detail.employer?.legalName?.trim() ?? extractEmployerFromLinkageReason(reason);

  if (employer && payor) {
    if (bucket === 'fail') {
      return `Bank deposit "${payor}" does not match declared employer "${employer}".`;
    }
    if (bucket === 'review') {
      return `Bank deposit "${payor}" versus "${employer}" needs manual review.`;
    }
    return `Bank deposit "${payor}" aligns with declared employer "${employer}".`;
  }

  const sentence = reason.split(/(?<=[.!?])\s+/)[0]?.trim();
  return sentence || 'Review payroll linkage against the declared employer.';
}

/** OPG-style adjudicator panel for Linkage gate checkpoint popovers. */
export function buildLinkageCheckpointView(input: {
  step: string;
  reason: string;
  bucket: string;
  detail: EmployerVerificationDetail;
  footprint?: EmployerFootprintAdminViewDto | null;
}): LinkageCheckpointView | null {
  const step = normalizeStep(input.step);
  if (!step) return null;

  if (step === 'AI_LINKAGE_ANALYSIS') {
    const ai = buildAiLinkageAdjudicatorView({
      reason: input.reason,
      detail: input.detail,
    });
    const meta = issueMetaFromBucket(input.bucket);
    return {
      popoverTitle: STEP_POPOVER_TITLES[step],
      dataPoints: ai.dataPoints,
      issueStatement: ai.issueStatement,
      issueSeverity: meta.severity,
      issueLabel: meta.label,
      footerNote: ai.footerNote,
      advisoryLabel: ai.advisoryLabel,
      advisorySeverity: ai.advisorySeverity,
    };
  }

  const evidenceRecord =
    input.detail.evidence != null
      ? (input.detail.evidence as Record<string, unknown>)
      : null;
  const compared = evidenceRecord
    ? comparedValuesForCheckpointStep(step, evidenceRecord)
    : [];
  const meta = issueMetaFromBucket(input.bucket);

  return {
    popoverTitle: STEP_POPOVER_TITLES[step],
    dataPoints: buildDataPointsForStep(step, {
      detail: input.detail,
      compared,
      reason: input.reason,
    }),
    issueStatement: buildIssueStatement(step, input.reason, input.bucket, input.detail),
    issueSeverity: meta.severity,
    issueLabel: meta.label,
    footerNote: null,
    advisoryLabel: null,
    advisorySeverity: 'default',
  };
}
